import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  ArrowLeft,
  Download,
  Image as ImageIcon,
  Video,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw
} from "lucide-react";

const ShootDetail = () => {
  const { shootId } = useParams();
  const [shoot, setShoot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShoot();
    // Poll for updates if processing
    const interval = setInterval(() => {
      if (shoot?.status === "processing" || shoot?.status === "queued") {
        fetchShoot();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [shootId, shoot?.status]);

  const fetchShoot = async () => {
    try {
      const response = await axios.get(`${API}/shoots/${shootId}`, { withCredentials: true });
      setShoot(response.data);
    } catch (error) {
      toast.error("Failed to load shoot details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "text-green-600 bg-green-50";
      case "processing": return "text-blue-600 bg-blue-50";
      case "queued": return "text-yellow-600 bg-yellow-50";
      case "failed": return "text-red-600 bg-red-50";
      default: return "text-neutral-600 bg-neutral-50";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": return <CheckCircle className="w-5 h-5" />;
      case "processing": return <RefreshCw className="w-5 h-5 animate-spin" />;
      case "queued": return <Clock className="w-5 h-5" />;
      case "failed": return <AlertCircle className="w-5 h-5" />;
      default: return null;
    }
  };

  const handleDownload = (url, filename) => {
    const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;
    const link = document.createElement('a');
    link.href = fullUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!shoot) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-neutral-600">Shoot not found</p>
          <Link to="/shoots">
            <Button className="mt-4">Back to Shoots</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const images = shoot.assets?.filter(a => a.asset_type === "image") || [];
  const videos = shoot.assets?.filter(a => a.asset_type === "video") || [];

  return (
    <DashboardLayout>
      <div data-testid="shoot-detail-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link to="/shoots">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-neutral-900">
              {shoot.sku?.sku_name || "Shoot Details"}
            </h1>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mt-2 ${getStatusColor(shoot.status)}`}>
              {getStatusIcon(shoot.status)}
              <span className="capitalize">{shoot.status}</span>
            </div>
          </div>
        </div>

        {/* Processing State */}
        {(shoot.status === "processing" || shoot.status === "queued") && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
              </div>
              <div>
                <p className="font-medium text-blue-900">AI Generation in Progress</p>
                <p className="text-sm text-blue-700">
                  {shoot.status === "queued" ? "Your shoot is queued for processing..." : "Generating images and videos. This may take a few minutes..."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Failed State */}
        {shoot.status === "failed" && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-900">Generation Failed</p>
                <p className="text-sm text-red-700">
                  {shoot.error_message || "An error occurred during generation. Please try again."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Shoot Info */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-neutral-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-neutral-500 font-normal">Product</CardTitle>
            </CardHeader>
            <CardContent>
              <img 
                src={shoot.sku?.uploaded_image_url?.startsWith('/') 
                  ? `${window.location.origin}${shoot.sku.uploaded_image_url}` 
                  : shoot.sku?.uploaded_image_url || "https://placehold.co/200x200"}
                alt={shoot.sku?.sku_name}
                className="w-full aspect-square object-cover rounded-lg mb-3"
              />
              <p className="font-medium">{shoot.sku?.sku_name}</p>
              <p className="text-sm text-neutral-500 capitalize">{shoot.sku?.category}</p>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-neutral-500 font-normal">AI Model</CardTitle>
            </CardHeader>
            <CardContent>
              <img 
                src={shoot.model?.preview_image_url || "https://placehold.co/200x200"}
                alt={shoot.model?.name}
                className="w-full aspect-square object-cover rounded-lg mb-3"
              />
              <p className="font-medium">{shoot.model?.name}</p>
              <p className="text-sm text-neutral-500 capitalize">
                {shoot.model?.gender} • {shoot.model?.age_group} • {shoot.model?.complexion}
              </p>
            </CardContent>
          </Card>

          <Card className="border-neutral-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-neutral-500 font-normal">Background</CardTitle>
            </CardHeader>
            <CardContent>
              <img 
                src={shoot.background?.preview_image_url || "https://placehold.co/200x200"}
                alt={shoot.background?.name}
                className="w-full aspect-video object-cover rounded-lg mb-3"
              />
              <p className="font-medium">{shoot.background?.name}</p>
              <p className="text-sm text-neutral-500 capitalize">{shoot.background?.category}</p>
            </CardContent>
          </Card>
        </div>

        {/* Generated Images */}
        {images.length > 0 && (
          <Card className="border-neutral-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-indigo-600" />
                Generated Images ({images.length})
              </CardTitle>
              <Button 
                variant="outline"
                size="sm"
                onClick={() => images.forEach((img, i) => handleDownload(img.asset_url, `image_${i+1}.png`))}
              >
                <Download className="w-4 h-4 mr-2" />
                Download All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {images.map((img, index) => (
                  <div key={img.asset_id} className="group relative">
                    <img 
                      src={img.asset_url?.startsWith('/') ? `${window.location.origin}${img.asset_url}` : img.asset_url}
                      alt={`Generated ${index + 1}`}
                      className="w-full aspect-[3/4] object-cover rounded-lg"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                      <Button 
                        size="sm"
                        onClick={() => handleDownload(img.asset_url, `image_${index+1}.png`)}
                        className="bg-white text-neutral-900 hover:bg-neutral-100"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                    {img.pose_type && (
                      <span className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 text-white text-xs rounded-full capitalize">
                        {img.pose_type.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generated Videos */}
        {videos.length > 0 && (
          <Card className="border-neutral-100">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-display text-xl flex items-center gap-2">
                <Video className="w-5 h-5 text-violet-600" />
                Generated Videos ({videos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {videos.map((vid, index) => (
                  <div key={vid.asset_id} className="relative bg-neutral-900 rounded-lg overflow-hidden aspect-video">
                    {vid.asset_url?.includes('placehold') ? (
                      <div className="absolute inset-0 flex items-center justify-center text-white">
                        <Video className="w-12 h-12 opacity-50" />
                      </div>
                    ) : (
                      <video 
                        src={vid.asset_url?.startsWith('/') ? `${window.location.origin}${vid.asset_url}` : vid.asset_url}
                        controls
                        className="w-full h-full object-cover"
                      />
                    )}
                    <Button 
                      size="sm"
                      onClick={() => handleDownload(vid.asset_url, `video_${index+1}.mp4`)}
                      className="absolute bottom-3 right-3 bg-white text-neutral-900 hover:bg-neutral-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Assets Yet */}
        {shoot.status !== "failed" && images.length === 0 && videos.length === 0 && (
          <Card className="border-neutral-100">
            <CardContent className="py-16 text-center">
              <div className="w-16 h-16 bg-neutral-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <ImageIcon className="w-8 h-8 text-neutral-400" />
              </div>
              <p className="text-neutral-600">
                {shoot.status === "processing" || shoot.status === "queued"
                  ? "Assets will appear here once generation is complete"
                  : "No assets generated yet"
                }
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ShootDetail;
